import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useBrands, setBrandSuper } from './TagBrandBits';
import { SALES_ACCESS } from './TagActivityWidget';
import { setAlterRight } from '@/lib/tagAlters';
import { TagAssistiveTouch } from './TagAssistiveTouch';
import { DashAccess } from './TagWidgetView';
import { useSearchParams } from 'react-router-dom';
import { notesApi } from '@/lib/notesApi';
import { loadRateCards } from '@/lib/rateCards';
import { loadGoldRate } from '@/lib/goldRate';
import { setRatePerms, setPricing, MissingRatesHost, addStoneRates, applyListStones, persistListStones } from '@/lib/tagPricing';
import { useNotesAuth } from '@/lib/notesAuth';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { Loader2, Diamond, AlertTriangle, RotateCcw, Trash2, Plus, X, Printer } from 'lucide-react';
import { TagWorkspaceShell } from './TagWorkspaceShell';
import { TagToolbar } from './TagToolbar';
import { TagJewelleryCard } from './TagJewelleryCard';
import { TagChargesDialog } from './TagExtraCharges';
import { TagRefreshTimer } from './TagRefreshTimer';
import { TagListBuilder } from './TagListBuilder';
import { TagSelectionPanel } from './TagSelectionPanel';
import { TagManualTagDialog } from './TagManualTagDialog';
import { tagThemeClass } from './tagTheme';
import { TagSingleTagDialog } from './TagSingleTagDialog';
import { TagLineFixHost, zeroPieceCount, openLineFix } from './TagLineFixDialog';
import { useNewPieces, TagNewPiecesNotice } from './TagNewPiecesNotice';
import { TagFixLogDialog } from './TagFixLogDialog';
import { TagReceiveConfirm } from './TagReceiveConfirm';
import { TagPhraseUnlock } from './TagLockedScreen';
import { PackageCheck } from 'lucide-react';
import { annotateSets, setSetRoles } from '@/lib/tagSets';
import { TagPagination } from './TagPagination';
import { TagSuperBar } from './TagSuperBar';
import { TagMobileNav, TagMobileMore } from './TagMobileNav';
import { TagImportDialog } from './TagImportDialog';
import { TagHelpDialog } from './TagHelpDialog';
import { TagFitCheck } from './TagFitCheck';
import { reloadLabelPresets } from '@/lib/tagPresets';
import { TagUsersAdmin } from './TagUsersAdmin';
import { TagSettingsDialog } from './TagSettingsDialog';
import { TagViewPasswordsAdmin } from './TagViewPasswordsAdmin';
import { TagRulesAdmin } from './TagRulesAdmin';
import { TagScanDialog } from './TagScanDialog';
import { TagSavedLists } from './TagSavedLists';
import { TagRateCards } from './TagRateCards';
import { ListChecks, Coins, History } from 'lucide-react';
import { TagDeleteDialog } from './TagDeleteDialog';
import { TagRestoreDialog, useRestoreShortcut } from './TagRestoreDialog';
import { TagBackupDialog } from './TagBackupDialog';
import { TagPortalLockDialog } from './TagPortalLockDialog';
import { TagSummaryAskHost } from './TagSummaryAsk';
import { TagLockFab } from './TagLockFab';
import { TagSaleDialog } from './TagSaleDialog';
import { TagSalesScreen } from './TagSalesScreen';
import { TagStockDashboard } from './TagStockDashboard';
import { TagApprovalsScreen } from './TagApprovalsScreen';
import { loadApprovals, returnFromApproval, approvalLock } from '@/lib/tagApprovals';
import { TagStockPanel } from './TagStockPanel';
import { TagDeleteSelected } from './TagDeleteSelected';
import { TagReturnDialogHost, openReturn } from './TagReturnDialog';
import { BadgeIndianRupee } from 'lucide-react';
import { Lock, UserCheck } from 'lucide-react';
import { TagCustomerChangeDialog } from './TagCustomerChange';
import { TagMyDashboard } from './TagMyDashboard';
import { TagRepairHistoryDialog } from './TagRepairHistoryDialog';
import { useTripleKey, usePortalLock, useSessionState, PORTAL_MODE } from './useTagPortal';
import { setTypeOrder } from '@/lib/tagGroups';
import { setTagRules, setTagClassOrder, setTagLineFixer, useTagRules, rulesAffecting, ruleLabel } from '@/lib/tagRules';
import { buildTagDataFromDetail } from '@/components/jewellery/JewelleryTagViewer';
import { setMatRules } from '@/lib/tagMatHandling';
import { setShortcuts, useShortcutKeys, useShortcutHints, useTagActions, setScPage, keyCodeOf } from '@/lib/tagShortcuts';
import { TagShortcutMenu } from './TagShortcuts';
import { fileToCappedDataUrl } from '@/lib/imageResize';
import { getNotesModuleCaps } from '@/lib/attachmentCaps';
import { JewelleryBatchPrintDialog, BATCH_TAG_LIMIT } from '@/components/jewellery/JewelleryBatchPrintDialog';

/**
 * TagWorkspacePage — the main (and only) authenticated page for the neutral
 * tag domain. No KPI/summary tiles. Shows company jewellery (read-only) plus
 * manual tags, with restricted card info (no value / goldsmith / order refs).
 */
const PAGE_SIZE = 30;
const MORE_CHUNK = 60;

const SEL_KEY = 'tagws_selection';
const useEscape = (on, close) => useEffect(() => {
  if (!on) return undefined;
  const k = (e) => { if (e.key === 'Escape') close(); };
  window.addEventListener('keydown', k);
  return () => window.removeEventListener('keydown', k);
}, [on, close]);

// Main-portal piece still waiting for 'Item Received' (pieces already out / sold are left alone).
export const notReceived = (j) => !!(j?.physical_pending && !j.sold && !j.approval);

// Every filtered card (all pages) in / out of the selection at once.
function SelectAllShown({ shown, selIds, setSelected }) {
  if (!shown.length) return null;
  const all = shown.every((j) => selIds.has(j.id));
  const some = !all && shown.some((j) => selIds.has(j.id));
  const toggle = () => setSelected((cur) => (all
    ? cur.filter((x) => !shown.some((j) => j.id === x.id))
    : [...cur, ...shown.filter((j) => !selIds.has(j.id)).map((j) => ({ id: j.id, jewellery_no: j.jewellery_no, customer: j.customer, made_for_id: j.made_for_id, repair: j.repair || null }))]));
  return (
    <label className="mb-3 inline-flex cursor-pointer items-center gap-2 text-sm text-foreground" data-testid="tagws-select-all-shown">
      <input type="checkbox" checked={all} ref={(el) => { if (el) el.indeterminate = some; }} onChange={toggle} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="tagws-select-all-shown-checkbox" />
      Select all shown <span className="text-xs text-muted-foreground">({shown.length})</span>
    </label>
  );
}

// Saved list + selection (no duplicates, max 30) → reviewed in the list editor before saving.
const mergeInto = (l, sel) => {
  const have = new Set(l.items.map((x) => x.id));
  const extra = sel.filter((x) => !have.has(x.id));
  const room = 30 - l.items.length;
  if (extra.length > room) toast.error(`A list can hold up to 30 pieces — added the first ${Math.max(room, 0)}.`);
  if (!extra.length) toast.info('All selected pieces are already in this list.');
  return { ...l, items: [...l.items, ...extra.slice(0, Math.max(room, 0))] };
};

function PickListDialog({ onClose, onPick }) {
  const [lists, setLists] = useState(null);
  const [q, setQ] = useState('');
  useEscape(true, onClose);
  useEffect(() => { notesApi.get('/notes/tag-workspace/tag-lists').then(({ data }) => setLists(data || [])).catch(() => setLists([])); }, []);
  const shown = (lists || []).filter((l) => l.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-3" data-testid="tagws-pick-list-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-card p-4 shadow-xl">
        <div className="mb-2 flex items-center justify-between"><h3 className="font-semibold text-foreground">Add selection to a saved list</h3><button onClick={onClose} aria-label="Close" data-testid="tagws-pick-list-close"><X size={18} /></button></div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lists…" className="mb-2 h-9 w-full rounded-lg border border-input bg-background px-3 text-sm" data-testid="tagws-pick-list-search" />
        <div className="max-h-80 space-y-1 overflow-y-auto">
          {lists === null ? <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 size={16} className="mx-auto animate-spin" /></div> : null}
          {shown.map((l) => (
            <button key={l.id} type="button" onClick={() => onPick(l)} className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2 text-left text-sm hover:bg-secondary" data-testid="tagws-pick-list-item">
              <span className="truncate font-medium text-foreground">{l.name}</span><span className="shrink-0 font-mono text-xs text-muted-foreground">{l.items.length}/30</span>
            </button>
          ))}
          {lists && !shown.length ? <p className="py-6 text-center text-sm text-muted-foreground">No saved lists.</p> : null}
        </div>
      </div>
    </div>
  );
}

// Active filters as chips; ✕ clears one, "Clear all" clears every chip shown.
function FilterChips({ chips, count, onPrint }) {
  const on = chips.filter(Boolean);
  if (!on.length) return null;
  return (
    <div className="flex w-full flex-wrap items-center gap-1.5" data-testid="tagws-filter-chips">
      {on.map((c) => (
        <span key={c.k} className="inline-flex max-w-[260px] items-center gap-1 rounded-full border border-[hsl(var(--ring))]/40 bg-[hsl(var(--ring))]/10 py-0.5 pl-2.5 pr-1 text-xs text-foreground" data-testid={`tagws-filter-chip-${c.k}`}>
          <span className="truncate">{c.label}</span>
          <button type="button" onClick={c.clear} aria-label={`Clear ${c.k} filter`} className="grid h-4 w-4 place-items-center rounded-full hover:bg-[hsl(var(--ring))]/20" data-testid={`tagws-filter-chip-${c.k}-clear`}><X size={11} /></button>
        </span>
      ))}
      {count ? (
        <button type="button" onClick={onPrint} className="ml-1 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground hover:opacity-90" title="Open batch printing with these filtered tags (you can still add or remove tags)" data-testid="tagws-filter-print-button">
          <Printer size={11} /> Print {count} filtered
        </button>
      ) : null}
      {on.length > 1 ? <button type="button" onClick={() => on.forEach((c) => c.clear())} className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline" data-testid="tagws-filter-chips-clear-all">Clear all</button> : null}
    </div>
  );
}

// Sales mode survives soft refreshes / remounts but not a full page reload or a new login.
const SALES_MODE = { on: false };

export default function TagWorkspacePage() {
  const { principal, logout } = useNotesAuth();
  const [params] = useSearchParams();
  const headerVariant = (params.get('header') || 'c').toLowerCase();

  const [ctx, setCtx] = useState(null);
  const [items, setItems] = useState([]);
  const [masters, setMasters] = useState({ gold_purities: [], jewellery_types: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  // Date filters stay hidden until Ctrl + D toggles them (hiding also clears them).
  const [showDates, setShowDates] = useState(false);
  // Goldsmith names on cards stay hidden until Ctrl + G toggles them.
  const [showSmith, setShowSmith] = useState(false);
  const [smithFilter, setSmithFilter] = useState('');
  useEffect(() => { if (!showSmith) setSmithFilter(''); }, [showSmith]);
  // Customer names on cards: Alt + C. Alt + A toggles dates, goldsmith and customer together.
  const [showCust, setShowCust] = useState(false);
  const [custFilter, setCustFilter] = useState('');
  const [madeFor, setMadeFor] = useState(false);
  const [custChange, setCustChange] = useState(null);
  const [repairOf, setRepairOf] = useState(null);
  const repairHistory = (it) => setRepairOf(it);
  const [typeFilter, setTypeFilter] = useState('');
  const [salesModeOn, setSalesModeState] = useState(() => SALES_MODE.on);
  const setSalesMode = (f) => setSalesModeState((v) => { const n = typeof f === 'function' ? f(v) : f; SALES_MODE.on = n; return n; });
  const [view, setView] = useState(() => (SALES_MODE.on && SALES_MODE.view) || 'stock');
  useEffect(() => { SALES_MODE.view = view; }, [view]);
  const [soldCust, setSoldCust] = useState('');
  const [soldSales, setSoldSales] = useState('');
  const [delAsk, setDelAsk] = useState(false);
  const canSellRef = useRef(false);
  const canDeleteRef = useRef(false);
  const delHits = useRef(0);
  const delTimer = useRef(null);
  const [saleOpen, setSaleOpen] = useState(false);
  const [saleFrom, setSaleFrom] = useState(null);
  const [salesOpen, setSalesOpen] = useState(false);
  const [approveFrom, setApproveFrom] = useState(null);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [apInit, setApInit] = useState(null); // { tab, cust } when opened from a dashboard row
  const [apSell, setApSell] = useState(null);
  const [purityFilter, setPurityFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [idFilter, setIdFilter] = useState(null); // { ids:Set, label } from a dashboard count
  const brandOk = (j) => !brandFilter || (brandFilter === '__none' ? !j.brand_name : j.brand_name === brandFilter);
  useEffect(() => { if (!showCust) setCustFilter(''); }, [showCust]);
  const allOnRef = useRef(false);
  allOnRef.current = showDates && showSmith && showCust;
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || e.ctrlKey || e.shiftKey || e.metaKey) return;
      if (keyCodeOf(e) === 'KeyD' && canDeleteRef.current) {
        // A single Alt + D deletes the selection; Alt + D D D (super admin restore) is left alone.
        e.preventDefault();
        delHits.current += 1;
        clearTimeout(delTimer.current);
        delTimer.current = setTimeout(() => { if (delHits.current === 1) setDelAsk(true); delHits.current = 0; }, 650);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (!e.ctrlKey || e.altKey || e.shiftKey || e.metaKey) return;
      const code = keyCodeOf(e);
      if (code === 'KeyG') { e.preventDefault(); setShowSmith((v) => !v); return; }
      if (code !== 'KeyD') return;
      e.preventDefault();
      setShowDates((v) => { if (v) { setDateFrom(''); setDateTo(''); } return !v; });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const [dateTo, setDateTo] = useState('');

  const [batchOpen, setBatchOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanIds, setScanIds] = useState(null);
  const [scanFmt, setScanFmt] = useState(null);
  const [listsOpen, setListsOpen] = useState(false);
  const [editList, setEditList] = useState(null);
  const [listsKey, setListsKey] = useState(0);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const viewIds = (ids, f) => { setScanIds(ids); setScanFmt(f); setBatchOpen(true); };
  useEffect(() => { const on = (e) => { setScanIds(e.detail.slice(0, BATCH_TAG_LIMIT)); setScanFmt(null); setBatchOpen(true); }; window.addEventListener('tagws-print-ids', on); return () => window.removeEventListener('tagws-print-ids', on); }, []);
  // Card selection survives filter changes until cleared.
  const [selected, setSelected] = useState(() => { try { return JSON.parse(sessionStorage.getItem(SEL_KEY) || '[]'); } catch { return []; } }); // [{ id, jewellery_no }]
  useEffect(() => { try { sessionStorage.setItem(SEL_KEY, JSON.stringify(selected)); } catch { /* noop */ } }, [selected]);
  const [listFromSel, setListFromSel] = useState(false);
  const [addToList, setAddToList] = useState(false); // false | true (pick) | list (merged, to review)
  const closeAdd = useCallback(() => setAddToList(false), []);
  const closeNewList = useCallback(() => setListFromSel(false), []);
  useEscape(!!addToList && addToList !== true, closeAdd);
  useEscape(listFromSel, closeNewList);
  const selIds = useMemo(() => new Set(selected.map((x) => x.id)), [selected]);
  const itemsById = useMemo(() => new Map(items.map((j) => [j.id, j])), [items]);
  // Pieces sent for repairing can't go on a new approval or sale.
  const repBlock = () => {
    if (recvBlock()) return true;
    const rep = selected.filter((x) => itemsById.get(x.id)?.repair?.in_repair).map((x) => x.jewellery_no);
    if (rep.length) toast.error(`Sent for repairing: ${rep.join(', ')}. Remove them from the selection to sell or send on approval.`, { id: 'in-repair' });
    return rep.length > 0;
  };
  // Keep the selection's customer / repair info in step with the latest list.
  useEffect(() => {
    setSelected((cur) => {
      let changed = false;
      const next = cur.map((s) => {
        const j = itemsById.get(s.id);
        if (!j || (JSON.stringify(j.repair || null) === JSON.stringify(s.repair || null) && j.customer === s.customer && j.made_for_id === s.made_for_id)) return s;
        changed = true;
        return { ...s, repair: j.repair || null, customer: j.customer, made_for_id: j.made_for_id };
      });
      return changed ? next : cur;
    });
  }, [itemsById]);
  // Main-portal pieces not yet ticked 'Item Received' may be selected (to mark them received in bulk) but not printed / sent / sold / listed.
  const recvBlock = () => {
    const bad = selected.filter((x) => notReceived(itemsById.get(x.id)));
    if (bad.length) toast.error(`Not received yet: ${bad.slice(0, 8).map((x) => x.jewellery_no).join(', ')}${bad.length > 8 ? ` +${bad.length - 8} more` : ''}. Mark them received first (Mark received in the selection panel).`, { id: 'not-received' });
    return bad.length > 0;
  };
  const selEntry = (it) => ({ id: it.id, jewellery_no: it.jewellery_no, customer: it.customer, made_for_id: it.made_for_id, repair: it.repair || null });
  const toggleSel = (it) => setSelected((cur) => (cur.some((x) => x.id === it.id) ? cur.filter((x) => x.id !== it.id) : [...cur, selEntry(it)]));
  // Set badge: select (or unselect) every piece of the set together.
  const selectSet = (members) => setSelected((cur) => {
    const ids = new Set(cur.map((x) => x.id));
    if (members.every((m) => ids.has(m.id))) return cur.filter((x) => !members.some((m) => m.id === x.id));
    return [...cur, ...members.filter((m) => !ids.has(m.id)).map(selEntry)];
  });
  const printSelected = () => {
    if (recvBlock()) return;
    if (selected.length > BATCH_TAG_LIMIT) toast.info(`Batch printing takes up to ${BATCH_TAG_LIMIT} tags — the first ${BATCH_TAG_LIMIT} selected are loaded.`);
    setScanIds(selected.map((x) => x.id).slice(0, BATCH_TAG_LIMIT)); setScanFmt(null); setBatchOpen(true);
  };
  // Filtered tags pre-loaded into batch printing; every piece stays available to add.
  const printFiltered = () => {
    if (filtered.length > BATCH_TAG_LIMIT) toast.info(`Batch printing takes up to ${BATCH_TAG_LIMIT} tags — the first ${BATCH_TAG_LIMIT} filtered tags are loaded.`);
    setScanIds(filtered.map((j) => j.id).slice(0, BATCH_TAG_LIMIT)); setScanFmt(null); setBatchOpen(true);
  };
  useEffect(() => {
    if (sessionStorage.getItem('tagws_open_scan')) { sessionStorage.removeItem('tagws_open_scan'); setScanOpen(true); }
  }, []);
  const [manual, setManual] = useState({ open: false, mode: 'create', item: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [tagItem, setTagItem] = useState(null);
  const [replacingId, setReplacingId] = useState(null);
  const [page, setPage] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [usersOpen, setUsersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Type suggestions stay current after Settings > Jewellery types or a manual tag save.
  const mastersFresh = useRef(true);
  useEffect(() => {
    if (settingsOpen || manual.open) { mastersFresh.current = false; return; }
    if (mastersFresh.current) return;
    mastersFresh.current = true;
    notesApi.get('/notes/tag-workspace/masters').then(({ data }) => setMasters(data || { gold_purities: [], jewellery_types: [] })).catch(() => {});
  }, [settingsOpen, manual.open]);
  const [pwOpen, setPwOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const loadRules = useCallback(() => {
    notesApi.get('/notes/tag-workspace/tag-rules').then((r) => setTagRules(r.data)).catch(() => {});
    notesApi.get('/notes/tag-workspace/tag-class-order').then((r) => setTagClassOrder(r.data)).catch(() => {});
    notesApi.get('/notes/tag-workspace/material-handling').then((r) => setMatRules(r.data.rules)).catch(() => {});
    notesApi.get('/notes/tag-workspace/shortcuts').then((r) => setShortcuts(r.data.shortcuts)).catch(() => {});
    notesApi.get('/notes/tag-workspace/tag-type-order').then((r) => setTypeOrder(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadRules(); }, [loadRules]);
  const [importOpen, setImportOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [fit, setFit] = useState(null);

  // First page of cards right away, the rest streamed in behind it.
  const genRef = useRef(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadRest = useCallback(async (gen, from, replace) => {
    setLoadingMore(true);
    let skip = from; let acc = [];
    try {
      for (;;) {
        const { data } = await notesApi.get('/notes/tag-workspace/jewellery', { params: { skip, limit: MORE_CHUNK } });
        if (gen !== genRef.current) return;
        const chunk = data || [];
        if (replace) acc = [...acc, ...chunk]; else setItems((list) => [...list, ...chunk]);
        skip += chunk.length;
        if (chunk.length < MORE_CHUNK) break;
      }
      if (replace) setItems(acc);
    } catch (e) { /* keep what is loaded */ } finally { if (gen === genRef.current) setLoadingMore(false); }
  }, []);
  const loadAll = useCallback(async () => {
    const gen = ++genRef.current;
    setLoading(true);
    setError('');
    try {
      const [cRes, jRes, mRes] = await Promise.all([
        notesApi.get('/notes/tag-workspace/context'),
        notesApi.get('/notes/tag-workspace/jewellery', { params: { limit: PAGE_SIZE } }),
        notesApi.get('/notes/tag-workspace/masters'),
      ]);
      if (gen !== genRef.current) return;
      setCtx(cRes.data); setBrandSuper(cRes.data?.is_tag_super || (cRes.data?.brand_ids || []).length > 1, cRes.data?.company_id);
      setRatePerms(cRes.data?.rates_view, cRes.data?.rates_edit); setTagLineFixer(cRes.data?.can_fix_lines); setAlterRight(cRes.data?.can_alter);
      const first = jRes.data || [];
      setItems(first);
      setMasters(mRes.data || { gold_purities: [], jewellery_types: [] });
      setLoading(false);
      if (first.length === PAGE_SIZE) loadRest(gen, PAGE_SIZE, false);
    } catch (e) {
      if (gen !== genRef.current) return;
      setError(e?.response?.status === 423 ? '' : extractError(e, 'Could not load the workspace.'));
      setLoading(false);
    }
  }, [loadRest]);
  useEffect(() => { loadAll(); }, [loadAll]);
  // Soft refresh: re-fetch every card in the background and swap them in (no spinner, page/filters kept).
  const [refreshing, setRefreshing] = useState(false);
  const softRefresh = useCallback(async () => {
    const gen = ++genRef.current;
    setRefreshing(true);
    try {
      notesApi.get('/notes/tag-workspace/context').then(({ data }) => { if (gen === genRef.current) { setCtx(data); setBrandSuper(data?.is_tag_super || (data?.brand_ids || []).length > 1, data?.company_id); setRatePerms(data?.rates_view, data?.rates_edit); setTagLineFixer(data?.can_fix_lines); setAlterRight(data?.can_alter); } }).catch(() => {});
      await loadRest(gen, 0, true);
    } finally { if (gen === genRef.current) setRefreshing(false); }
  }, [loadRest]);

  // A sale or sales return anywhere in this company (or another tab) soft-refreshes the cards right away.
  useEffect(() => {
    let last = null; let alive = true;
    const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('tagws-sales') : null;
    const check = () => notesApi.get('/notes/tag-workspace/sales/stamp').then(({ data }) => {
      if (!alive) return;
      if (last !== null && data.stamp !== last) softRefresh();
      last = data.stamp;
    }).catch(() => {});
    check();
    const iv = setInterval(() => { if (!document.hidden) check(); }, 15000);
    // This tab already refreshed itself: just note the new stamp and tell the other tabs.
    const onLocal = () => { notesApi.get('/notes/tag-workspace/sales/stamp').then(({ data }) => { last = data.stamp; }).catch(() => {}); bc?.postMessage('changed'); };
    window.addEventListener('tagws-sales-changed', onLocal);
    if (bc) bc.onmessage = () => check();
    return () => { alive = false; clearInterval(iv); window.removeEventListener('tagws-sales-changed', onLocal); bc?.close(); };
  }, [softRefresh]);

  // Live check (every minute): pieces added on the main portal are pulled in quietly.
  useEffect(() => {
    let last = null; let alive = true;
    const check = () => notesApi.get('/notes/tag-workspace/pieces/stamp').then(({ data }) => {
      if (!alive) return;
      if (last !== null && data.stamp !== last) softRefresh();
      last = data.stamp;
    }).catch(() => {});
    check();
    const iv = setInterval(() => { if (!document.hidden) check(); }, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [softRefresh, ctx?.company_id]);

  // Drop selected pieces that no longer exist (deleted / other company) once every card is loaded.
  useEffect(() => {
    if (loading || loadingMore || refreshing || (!items.length && !PORTAL_MODE.partial)) return;
    const ids = new Set(items.map((j) => j.id));
    setSelected((cur) => (cur.every((x) => ids.has(x.id)) ? cur : cur.filter((x) => ids.has(x.id))));
  }, [items, loading, loadingMore, refreshing]);
  const isTagSuper = !!ctx?.is_tag_super;
  const cardBrands = useBrands(!!ctx?.company_id, ctx?.company_id || '');
  const [keysOpen, setKeysOpen] = useState(false);
  useShortcutKeys(ctx);
  useShortcutHints(ctx);
  setScPage(approvalsOpen ? 'approvals' : salesOpen ? 'sales' : 'tags');
  // While a register is open on top, its own screen handles clear / filter keys.
  const behind = (fn) => () => { if (!approvalsOpen && !salesOpen) fn(); };
  useTagActions({
    clear_selection: behind(() => setSelected([])),
    clear_filters: behind(() => { setSearch(''); setDateFrom(''); setDateTo(''); setSmithFilter(''); setCustFilter(''); setTypeFilter(''); setPurityFilter(''); setSoldCust(''); setSoldSales(''); setRuleFilter(''); }),
    toggle_customer_filter: behind(() => setShowCust((v) => !v)),
    toggle_all_filters: behind(() => { const on = !allOnRef.current; setShowDates(on); setShowSmith(on); setShowCust(on); if (!on) { setDateFrom(''); setDateTo(''); } }),
    open_approvals: () => { if (ctx?.approvals_access || PORTAL_MODE.partial) setApprovalsOpen(true); },
    open_sales: () => { if ((ctx?.can_sell && !ctx?.sales_only && salesMode) || ctx?.sales_only) setSalesOpen(true); },
    open_lists: () => setListsOpen(true),
    shortcut_menu: () => setKeysOpen(true),
    go_home: () => { closeAll(); setDash(false); },
    toggle_sales_mode: () => { if (canSellRef.current && !PORTAL_MODE.partial) setSalesMode((v) => { toast.info(v ? 'Sales mode off.' : 'Sales mode on. Press the same keys (or the Assistive button) to turn it off.', { id: 'sales-mode' }); if (v) setView('stock'); else setTimeout(() => toSalesDashRef.current(), 0); return !v; }); },
  });
  // Super admin: Alt + G then H shows / hides the stock dashboard. Other users: their own dashboard of shared widgets.
  const [dash, setDash] = useState(false);
  const closeAll = () => { setApprovalsOpen(false); setSalesOpen(false); setListsOpen(false); setSettingsOpen(false); setKeysOpen(false); setBatchOpen(false); setScanOpen(false); };
  const closeRef = useRef(closeAll); closeRef.current = closeAll;
  const superRef = useRef(false); superRef.current = !!ctx?.is_tag_super;
  // Sales mode on: super admin -> Sales register dashboard; others -> their dashboard (with the sales widgets given to them).
  const [salesTab, setSalesTab] = useState('');
  const toSalesDashRef = useRef(() => {});
  toSalesDashRef.current = () => {
    if (ctx?.is_tag_super) { closeAll(); setDash(false); setSalesTab('dash'); setSalesOpen(true); }
    else if ((myGrants || []).some((g) => g.startsWith('sales:'))) { closeAll(); setDash(true); }
  };
  useEffect(() => {
    const f = (e) => {
      const v = e.detail?.sold;
      if (v === 'stock') setView('stock');
      else if (v === 'sold' && SALES_ACCESS.on) { setSalesMode(true); setView('sold'); }
      closeRef.current(); setDash(false); setIdFilter({ ids: new Set(e.detail?.ids || []), label: e.detail?.label || 'Selected pieces' }); window.scrollTo({ top: 0 }); };
    window.addEventListener('tagws-open-pieces', f);
    return () => window.removeEventListener('tagws-open-pieces', f);
  }, []);
  const [myGrants, setMyGrants] = useState(null); // null until loaded
  useEffect(() => {
    if (!ctx || isTagSuper) { setMyGrants([]); return; }
    notesApi.get('/notes/tag-workspace/dashboards/mine').then(({ data }) => setMyGrants(data.grants || [])).catch(() => setMyGrants([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.company_id, isTagSuper]); // eslint-disable-line react-hooks/exhaustive-deps
  const dashAllowed = !!ctx; // every user lands on a dashboard (theirs shows only the widgets shared with them)
  useEffect(() => {
    if (!dashAllowed) { setDash(false); return undefined; }
    let armed = 0;
    const onKey = (e) => {
      if (!e.altKey) return;
      const code = keyCodeOf(e);
      if (code === 'KeyQ' && superRef.current) { e.preventDefault(); closeRef.current(); setDash(false); return; }
      if (code === 'KeyG') { armed = Date.now(); e.preventDefault(); return; }
      if (code === 'KeyH' && Date.now() - armed < 1500) { armed = 0; e.preventDefault(); closeRef.current(); setDash(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dashAllowed]);
  // Everyone: Alt + G then L logs out of the portal.
  const logoutRef = useRef(null);
  logoutRef.current = () => { SALES_MODE.on = false; localStorage.removeItem('tagws_company'); logout(); };
  useEffect(() => {
    let armed = 0;
    const onKey = (e) => {
      if (!e.altKey) return;
      const code = keyCodeOf(e);
      if (code === 'KeyG') { armed = Date.now(); return; }
      if (code === 'KeyL' && Date.now() - armed < 1500) { armed = 0; e.preventDefault(); logoutRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Portal lock (Ctrl + Z Z Z): older sessions are logged out, newer ones see the expired notice.
  const { locked: portalLocked, partial: portalPartial, portalOpen } = usePortalLock({
    onUnlock: () => { loadAll(); loadRules(); },
    onEnded: () => { SALES_MODE.on = false; localStorage.removeItem('tagws_company'); logout(); },
  });
  // Sales & Approval mode: selling and approval actions stay on without the sales-mode toggle.
  const salesMode = salesModeOn || portalPartial;
  const onDash = dash && dashAllowed && !portalLocked;
  // Everyone with a dashboard lands on it (Stock dashboard / My dashboard) when the portal is fully unlocked (once per visit).
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current || portalOpen === null || !ctx || (!ctx.is_tag_super && myGrants === null)) return;
    landed.current = true;
    if (!portalLocked && dashAllowed) setDash(true);
  }, [portalOpen, portalLocked, ctx, myGrants, dashAllowed]);
  const salesOnly = !!ctx?.sales_only;
  const multiBrand = (ctx?.brand_ids || []).length > 1;
  const canSellNow = !!ctx?.can_sell && !salesOnly;
  const canApprove = (!!ctx?.rates_edit || isTagSuper) && !portalLocked && !salesOnly;
  useEffect(() => { if (portalLocked) { ++genRef.current; setItems([]); setLoading(false); setLoadingMore(false); setError(''); } }, [portalLocked]);
  const canDelete = !!(ctx?.is_tag_admin || ctx?.is_tag_super);
  const viewTotal = useMemo(() => items.filter((j) => (view === 'sold') === !!j.sold).length, [items, view]);
  canSellRef.current = !!canSellNow && !portalLocked;
  SALES_ACCESS.on = canSellRef.current && !PORTAL_MODE.partial;
  const regOk = !!(isTagSuper || ctx?.approvals_access || PORTAL_MODE.partial);
  const dashAccess = useMemo(() => ({ money: !!(isTagSuper || ctx?.rates_view || ctx?.rates_edit || ctx?.can_sell), register: regOk }), [isTagSuper, ctx, regOk]);
  const regRef = useRef(false); regRef.current = regOk;
  useEffect(() => {
    const f = (e) => { if (!regRef.current) return; setApInit(e.detail || null); setApprovalsOpen(true); };
    window.addEventListener('tagws-open-register', f);
    return () => window.removeEventListener('tagws-open-register', f);
  }, []);
  canDeleteRef.current = canDelete && selected.length > 0 && !portalLocked;
  const openRestore = useCallback(() => setRestoreOpen(true), []);
  useRestoreShortcut(isTagSuper, openRestore);
  const [backupOpen, setBackupOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const openBackup = useCallback(() => setBackupOpen(true), []);
  const openLock = useCallback(() => setLockOpen(true), []);
  const closeLock = useCallback(() => setLockOpen(false), []);
  useTripleKey('KeyB', isTagSuper && !portalLocked && !portalPartial, openBackup, true);
  useTripleKey('KeyZ', isTagSuper && !portalLocked, openLock);
  useEffect(() => {
    if (!isTagSuper) return;
    notesApi.get('/notes/tag-workspace/admin/companies').then(({ data }) => setCompanies(data || [])).catch(() => {});
  }, [isTagSuper]);

  const [chargesItem, setChargesItem] = useState(null);
  const brandSaved = (id, brand_id, brand_name) => setItems((list) => list.map((j) => (j.id === id ? { ...j, brand_id, brand_name } : j)));
  const partySaved = (id, kind, v, name) => setItems((list) => list.map((j) => (j.id === id ? { ...j, [`${kind}_id`]: v, [kind]: name } : j)));
  const chargesSaved = (id, extra_charges) => setItems((list) => list.map((j) => (j.id === id ? { ...j, extra_charges } : j)));
  const rememberLayout = (id, layout) => setItems((list) => list.map((j) => (j.id === id ? { ...j, tagws_layout: layout } : j)));

  const chooseCompany = (cid) => {
    if (cid) localStorage.setItem('tagws_company', cid); else localStorage.removeItem('tagws_company');
    reloadLabelPresets();
    setPage(1);
    loadAll();
    loadRules();
    setPricing({ cardId: '' });
    loadRateCards();
    loadGoldRate();
  };

  const reloadList = useCallback(() => loadRest(++genRef.current, 0, true), [loadRest]);
  useEffect(() => {
    window.addEventListener('tagws-pieces-changed', reloadList);
    return () => window.removeEventListener('tagws-pieces-changed', reloadList);
  }, [reloadList]);

  const tagRules = useTagRules();
  const [ruleFilter, setRuleFilter] = useState('');
  // Super admin: which active rules change each piece's tag.
  const ruleHits = useMemo(() => {
    if (!isTagSuper || !tagRules.length) return {};
    const out = {};
    items.forEach((it) => { out[it.id] = it.is_manual ? [] : rulesAffecting(buildTagDataFromDetail(it), tagRules).map((r) => r.id); });
    return out;
  }, [isTagSuper, tagRules, items]);
  const ruleUsage = useMemo(() => {
    const u = {};
    Object.values(ruleHits).forEach((ids) => ids.forEach((rid) => { u[rid] = (u[rid] || 0) + 1; }));
    return u;
  }, [ruleHits]);
  // Pieces alert (line fixers): tag lines with carats but 0 pieces.
  const lineFixer = !!ctx?.can_fix_lines;
  const [zeroOnly, setZeroOnly] = useState(false);
  const zeroPcs = useMemo(() => {
    if (!lineFixer) return {};
    const out = {};
    items.forEach((it) => { if (!it.sold) { const n = zeroPieceCount(it); if (n) out[it.id] = n; } });
    return out;
  }, [lineFixer, items, tagRules]); // eslint-disable-line react-hooks/exhaustive-deps
  const zeroN = Object.keys(zeroPcs).length;
  const zeroOn = zeroOnly && lineFixer && view !== 'sold';
  // New main-portal pieces since this login's last refresh.
  const [freshIds, closeFresh] = useNewPieces(ctx, items, !loading && !loadingMore && !refreshing && items.length > 0);
  const [newOnly, setNewOnly] = useState(null);
  const [fixLogOpen, setFixLogOpen] = useState(false);
  // Physical receipt of main-portal pieces (tag admins + super admin).
  const canReceive = !!(ctx?.is_tag_admin || ctx?.is_tag_super);
  const [physOnly, setPhysOnly] = useState(false);
  const physN = useMemo(() => (canReceive ? items.filter((j) => j.physical_pending && !j.sold).length : 0), [canReceive, items]);
  const physOn = physOnly && canReceive && view !== 'sold' && physN > 0;
  const [recvAsk, setRecvAsk] = useState(null);
  const askReceive = (list) => setRecvAsk(list.filter((j) => j?.physical_pending && !j.sold).map((j) => ({ id: j.id, jewellery_no: j.jewellery_no })));
  const markReceived = (it) => askReceive([it]);
  const confirmReceive = async (pieces) => {
    try {
      const { data } = await notesApi.post('/notes/tag-workspace/jewellery-received/bulk', { ids: pieces.map((p) => p.id) });
      const done = new Set(pieces.map((p) => p.id));
      setItems((list) => list.map((j) => (done.has(j.id) ? { ...j, physical_pending: false } : j)));
      toast.success(`${data.marked || pieces.length} item(s) marked as received.`);
      setRecvAsk(null);
    } catch (e) { toast.error(extractError(e, 'Could not mark as received')); }
  };
  const ruleOn = isTagSuper && tagRules.some((r) => r.id === ruleFilter) ? ruleFilter : '';

  // Cascading filters: each filter's options come from the pieces left by all the other filters.
  const passes = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00+05:30`).getTime() : null;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59+05:30`).getTime() : null;
    return (j, skip) => {
      if ((view === 'sold') !== !!j.sold) return false;
      if (madeFor && view !== 'sold' && !j.customer) return false;
      if (view === 'sold' && skip !== 'scust' && soldCust && j.sold.customer_name !== soldCust) return false;
      if (view === 'sold' && skip !== 'ssales' && soldSales && !(soldSales === '__none' ? !(j.sold.sales_names || []).length : (j.sold.sales_names || []).includes(soldSales))) return false;
      if (ruleOn && !(ruleHits[j.id] || []).includes(ruleOn)) return false;
      if (zeroOn && !zeroPcs[j.id]) return false;
      if (newOnly && !newOnly.has(j.id)) return false;
      if (physOn && !j.physical_pending) return false;
      if (skip !== 'smith' && smithFilter && (smithFilter === '__none' ? !!j.goldsmith : j.goldsmith !== smithFilter)) return false;
      if (skip !== 'cust' && custFilter && (custFilter === '__none' ? !!j.customer : j.customer !== custFilter)) return false;
      if (idFilter) return idFilter.ids.has(j.id);
      if (skip !== 'brand' && !brandOk(j)) return false;
      if (skip !== 'purity' && purityFilter && (purityFilter === '__none' ? !!j.gold_purity_name : j.gold_purity_name !== purityFilter)) return false;
      if (skip !== 'type' && typeFilter && (typeFilter === '__none' ? !!j.jewellery_type : (j.set_type || j.jewellery_type) !== typeFilter)) return false;
      if (fromMs != null || toMs != null) {
        const ts = j.received_at ? new Date(j.received_at).getTime() : null;
        if (ts == null) return false;
        if (fromMs != null && ts < fromMs) return false;
        if (toMs != null && ts > toMs) return false;
      }
      if (!q) return true;
      const gross = (j.gross_weight_grams || 0).toFixed(3);
      return (
        (j.jewellery_no || '').toLowerCase().includes(q)
        || (j.title || '').toLowerCase().includes(q)
        || (j.jewellery_type || '').toLowerCase().includes(q)
        || (j.gold_purity_name || '').toLowerCase().includes(q)
        || gross.includes(q)
      );
    };
  }, [search, dateFrom, dateTo, ruleOn, ruleHits, smithFilter, custFilter, typeFilter, purityFilter, brandFilter, idFilter, view, soldCust, soldSales, madeFor, zeroOn, zeroPcs, newOnly, physOn]); // eslint-disable-line react-hooks/exhaustive-deps
  // Necklace / Pendant sets (same number before the last dash) for the type filter and dashboards.
  const setAware = useMemo(() => { setSetRoles(ctx?.jtype_sets); return annotateSets(items, ctx?.jtype_sets); }, [items, ctx?.jtype_sets]);
  const setMembers = useMemo(() => { const m = {}; setAware.forEach((j) => { if (j.set_id) (m[j.set_id] ||= []).push(j); }); return m; }, [setAware]);
  const filtered = useMemo(() => setAware.filter((j) => passes(j)), [setAware, passes]);
  const shownPend = physOn ? filtered.filter((j) => j.physical_pending && !j.sold).length : 0;
  const madeForN = useMemo(() => items.filter((j) => !j.sold && j.customer).length, [items]);

  useEffect(() => { setPage(1); }, [search, dateFrom, dateTo, ruleOn, smithFilter, custFilter, typeFilter, purityFilter, brandFilter, idFilter, madeFor, zeroOn, newOnly]);
  const optionsOf = (key, skip) => {
    const c = {};
    setAware.forEach((j) => { if (!passes(j, skip)) return; const k = (key === 'jewellery_type' ? j.set_type || j.jewellery_type : j[key]) || '__none'; c[k] = (c[k] || 0) + 1; });
    return Object.keys(c).filter((k) => k !== '__none').sort((a, b) => a.localeCompare(b)).map((k) => [k, c[k]]).concat(c.__none ? [['__none', c.__none]] : []);
  };
  // Keep the current choice listed (0 pieces) even when other filters leave none of it.
  const withSel = (opts, sel) => (sel && !opts.some(([k]) => k === sel) ? [...opts, [sel, 0]] : opts);
  const smithOptions = useMemo(() => withSel(optionsOf('goldsmith', 'smith'), smithFilter), [items, passes, smithFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const soldOptions = (skip, valuesOf) => {
    if (view !== 'sold') return [];
    const c = {};
    items.forEach((j) => { if (!passes(j, skip)) return; const vs = valuesOf(j); (vs.length ? vs : ['__none']).forEach((k) => { c[k] = (c[k] || 0) + 1; }); });
    return Object.keys(c).filter((k) => k !== '__none').sort((a, b) => a.localeCompare(b)).map((k) => [k, c[k]]).concat(c.__none ? [['__none', c.__none]] : []);
  };
  const soldCustOptions = useMemo(() => withSel(soldOptions('scust', (j) => [j.sold.customer_name].filter(Boolean)), soldCust), [items, passes, soldCust, view]); // eslint-disable-line react-hooks/exhaustive-deps
  const soldSalesOptions = useMemo(() => withSel(soldOptions('ssales', (j) => j.sold.sales_names || []), soldSales), [items, passes, soldSales, view]); // eslint-disable-line react-hooks/exhaustive-deps
  const typeOptions = useMemo(() => withSel(optionsOf('jewellery_type', 'type'), typeFilter), [items, passes, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const purityOptions = useMemo(() => withSel(optionsOf('gold_purity_name', 'purity'), purityFilter), [items, passes, purityFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const brandOptions = useMemo(() => withSel(optionsOf('brand_name', 'brand'), brandFilter), [items, passes, brandFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const custOptions = useMemo(() => withSel(optionsOf('customer', 'cust'), custFilter), [items, passes, custFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const goPage = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const replaceImage = async (item, file) => {
    setReplacingId(item.id);
    try {
      const caps = await getNotesModuleCaps();
      const dataUrl = await fileToCappedDataUrl(file, caps?.erp_kb);
      const { data } = await notesApi.put(`/notes/tag-workspace/jewellery/${item.id}/image`, { image: dataUrl });
      setItems((list) => list.map((j) => (j.id === item.id ? { ...j, image: data.image } : j)));
      toast.success('Image replaced.');
    } catch (e) {
      toast.error(extractError(e, 'Could not replace the image.'));
    } finally {
      setReplacingId(null);
    }
  };

  // Where the user left off (selection, filters, page) — kept on the server across logins.
  const [pendingPage, setPendingPage] = useState(null);
  useSessionState(!portalLocked, { selected, search, typeFilter, purityFilter, smithFilter, custFilter, dateFrom, dateTo, showDates, showSmith, showCust, page }, (st) => {
    if (Array.isArray(st.selected)) setSelected(st.selected);
    setSearch(st.search || ''); setTypeFilter(st.typeFilter || ''); setPurityFilter(st.purityFilter || '');
    setShowDates(!!st.showDates); setShowSmith(!!st.showSmith); setShowCust(!!st.showCust);
    setSmithFilter(st.smithFilter || ''); setCustFilter(st.custFilter || ''); setDateFrom(st.dateFrom || ''); setDateTo(st.dateTo || '');
    if (st.page > 1) setPendingPage(st.page);
  });
  useEffect(() => { if (pendingPage && !loading && !loadingMore) { setPage(pendingPage); setPendingPage(null); } }, [pendingPage, loading, loadingMore]);

  const onReset = () => { setSearch(''); setDateFrom(''); setDateTo(''); setRuleFilter(''); setTypeFilter(''); setPurityFilter(''); setSmithFilter(''); setCustFilter(''); setMadeFor(false); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await notesApi.delete(`/notes/tag-workspace/manual/${deleteTarget.id}`);
      toast.success('Tag deleted.');
      setDeleteTarget(null);
      reloadList();
    } catch (e) {
      toast.error(extractError(e, 'Could not delete the tag.'));
    } finally {
      setDeleting(false);
    }
  };

  const toolbar = (
    <TagToolbar
      search={search} setSearch={setSearch}
      showDates={showDates} dateFrom={dateFrom} setDateFrom={setDateFrom}
      dateTo={dateTo} setDateTo={setDateTo}
      onReset={onReset} count={filtered.length} total={viewTotal}
      canCreate={!!ctx && !salesOnly}
      onCreate={() => setManual({ open: true, mode: 'create', item: null })}
      onBatch={() => setBatchOpen(true)} batchDisabled={filtered.length === 0}
      onImport={() => setImportOpen(true)}
      railActions={(headerVariant || '').toLowerCase() === 'c'}
      extraFilter={<>
        {view === 'sold' ? (
          <>
            <select value={soldCust} onChange={(e) => { setSoldCust(e.target.value); setPage(1); }} title="Sold to customer"
              className={`h-9 max-w-[180px] rounded-lg border px-2 text-sm ${soldCust ? 'border-emerald-600 bg-emerald-600/5 font-medium' : 'border-border bg-background'} text-foreground`} data-testid="tagws-sold-customer-filter">
              <option value="">All buyers</option>
              {soldCustOptions.map(([k, n]) => <option key={k} value={k}>{k} ({n})</option>)}
            </select>
            <select value={soldSales} onChange={(e) => { setSoldSales(e.target.value); setPage(1); }} title="Sales person"
              className={`h-9 max-w-[180px] rounded-lg border px-2 text-sm ${soldSales ? 'border-emerald-600 bg-emerald-600/5 font-medium' : 'border-border bg-background'} text-foreground`} data-testid="tagws-sold-sales-filter">
              <option value="">All sales persons</option>
              {soldSalesOptions.map(([k, n]) => <option key={k} value={k}>{k === '__none' ? 'No sales person' : k} ({n})</option>)}
            </select>
          </>
        ) : (
          madeForN ? <button type="button" onClick={() => setMadeFor((v) => !v)} aria-pressed={madeFor} title="Pieces made for a customer (not own stock) whose sale is not recorded yet"
            className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${madeFor ? 'border-amber-600 bg-amber-600 text-white' : 'border-amber-500/50 bg-amber-500/10 text-amber-800 hover:bg-amber-500/20 dark:text-amber-300'}`} data-testid="tagws-made-for-chip">
            <UserCheck size={13} /> P D <span className={`rounded-full px-1.5 font-mono ${madeFor ? 'bg-white/25' : 'bg-amber-500/20'}`} data-testid="tagws-made-for-count">{madeForN}</span></button> : null
        )}
        {physN && view !== 'sold' ? <button type="button" onClick={() => setPhysOnly((v) => !v)} aria-pressed={physOn} title="Main-portal pieces not yet received by the sales department"
          className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${physOn ? 'border-red-600 bg-red-600 text-white' : 'border-red-400/60 bg-red-500/10 text-red-700 hover:bg-red-500/20 dark:text-red-300'}`} data-testid="tagws-physical-pending-chip">
          <span className={`h-2 w-2 rounded-full ${physOn ? 'bg-white' : 'bg-red-600'}`} /> Physical Pending <span className={`rounded-full px-1.5 font-mono ${physOn ? 'bg-white/25' : 'bg-red-500/20'}`} data-testid="tagws-physical-pending-count">{physN}</span></button> : null}
        {physOn && !portalLocked && shownPend ? <button type="button" onClick={() => askReceive(filtered)} title="Mark every piece in the current view as received"
          className="inline-flex h-9 items-center gap-1.5 rounded-full bg-emerald-700 px-3 text-xs font-semibold text-white shadow-sm transition-transform duration-150 hover:-translate-y-0.5" data-testid="tagws-receive-all-shown">
          <PackageCheck size={13} /> Mark all shown received ({shownPend})</button> : null}
        {zeroN && view !== 'sold' ? <button type="button" onClick={() => setZeroOnly((v) => !v)} aria-pressed={zeroOn} title="Tags with lines that have carats but 0 pieces"
          className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${zeroOn ? 'border-red-600 bg-red-600 text-white' : 'border-red-400/60 bg-red-500/10 text-red-700 hover:bg-red-500/20 dark:text-red-300'}`} data-testid="tagws-zero-pcs-chip">
          <AlertTriangle size={13} /> 0 pieces on tag <span className={`rounded-full px-1.5 font-mono ${zeroOn ? 'bg-white/25' : 'bg-red-500/20'}`} data-testid="tagws-zero-pcs-count">{zeroN}</span></button> : null}
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} title="Show one type of jewellery"
          className={`h-9 max-w-[180px] rounded-lg border px-2 text-sm ${typeFilter ? 'border-[hsl(var(--ring))] bg-[hsl(var(--ring))]/5 font-medium' : 'border-border bg-background'} text-foreground`} data-testid="tagws-type-filter">
          <option value="">All types</option>
          {typeOptions.map(([k, n]) => <option key={k} value={k}>{k === '__none' ? 'No type' : k} ({n})</option>)}
        </select>
        <select value={purityFilter} onChange={(e) => setPurityFilter(e.target.value)} title="Show one gold purity"
          className={`h-9 max-w-[160px] rounded-lg border px-2 text-sm ${purityFilter ? 'border-[hsl(var(--ring))] bg-[hsl(var(--ring))]/5 font-medium' : 'border-border bg-background'} text-foreground`} data-testid="tagws-purity-filter">
          <option value="">All purities</option>
          {purityOptions.map(([k, n]) => <option key={k} value={k}>{k === '__none' ? 'No purity' : k} ({n})</option>)}
        </select>
        {(isTagSuper || multiBrand) && brandOptions.length ? (
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} title="Show one brand"
            className={`h-9 max-w-[160px] rounded-lg border px-2 text-sm ${brandFilter ? 'border-[hsl(var(--ring))] bg-[hsl(var(--ring))]/5 font-medium' : 'border-border bg-background'} text-foreground`} data-testid="tagws-brand-filter">
            <option value="">All brands</option>
            {brandOptions.map(([k, n]) => <option key={k} value={k}>{k === '__none' ? 'No brand' : k} ({n})</option>)}
          </select>
        ) : null}
        {showSmith ? (
          <select value={smithFilter} onChange={(e) => setSmithFilter(e.target.value)} title="Show one goldsmith's pieces (Ctrl + G hides this)"
            className={`h-9 max-w-[200px] rounded-lg border px-2 text-sm ${smithFilter ? 'border-[hsl(var(--ring))] bg-[hsl(var(--ring))]/5 font-medium' : 'border-border bg-background'} text-foreground`} data-testid="tagws-goldsmith-filter">
            <option value="">All goldsmiths</option>
            {smithOptions.map(([k, n]) => <option key={k} value={k}>{k === '__none' ? 'No goldsmith' : k} ({n})</option>)}
          </select>
        ) : null}
        {showCust ? (
          <select value={custFilter} onChange={(e) => setCustFilter(e.target.value)} title="Show one customer's pieces (Alt + U hides this)"
            className={`h-9 max-w-[200px] rounded-lg border px-2 text-sm ${custFilter ? 'border-[hsl(var(--ring))] bg-[hsl(var(--ring))]/5 font-medium' : 'border-border bg-background'} text-foreground`} data-testid="tagws-customer-filter">
            <option value="">All customers</option>
            {custOptions.map(([k, n]) => <option key={k} value={k}>{k === '__none' ? 'No customer' : k} ({n})</option>)}
          </select>
        ) : null}
        <TagRefreshTimer onRefresh={softRefresh} busy={refreshing} />

        {lineFixer ? <button type="button" onClick={() => setFixLogOpen(true)} className="hidden h-9 md:inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-secondary" data-testid="tagws-fix-log-open-button"><History size={15} /> <span className="hidden sm:inline">Corrections log</span></button> : null}
        {portalPartial ? null : <button type="button" onClick={() => setListsOpen(true)} className="hidden h-9 md:inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-secondary" data-testid="tagws-saved-lists-open-button"><ListChecks size={15} /> <span className="hidden sm:inline">Saved lists</span></button>}
        {ctx?.rates_view ? <button type="button" onClick={() => setRatesOpen(true)} className="hidden h-9 md:inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-secondary" data-testid="tagws-rate-cards-open-button"><Coins size={15} /> <span className="hidden sm:inline">Rates</span></button> : null}
        {isTagSuper && tagRules.length ? (
        <select value={ruleOn} onChange={(e) => setRuleFilter(e.target.value)} title="Show only pieces a rule changes"
          className="h-9 max-w-[220px] rounded-lg border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" data-testid="tagws-filter-rule-select">
          <option value="">All rules</option>
          {tagRules.map((r) => <option key={r.id} value={r.id}>{ruleLabel(r)}</option>)}
        </select>
      ) : null}
              <FilterChips chips={[
          typeFilter && { k: 'type', label: `Type: ${typeFilter === '__none' ? 'No type' : typeFilter}`, clear: () => setTypeFilter('') },
          idFilter && { k: 'ids', label: `${idFilter.label} (${idFilter.ids.size})`, clear: () => setIdFilter(null) },
          brandFilter && { k: 'brand', label: `Brand: ${brandFilter === '__none' ? 'No brand' : brandFilter}`, clear: () => setBrandFilter('') },
          purityFilter && { k: 'purity', label: `Purity: ${purityFilter === '__none' ? 'No purity' : purityFilter}`, clear: () => setPurityFilter('') },
          smithFilter && { k: 'smith', label: `Goldsmith: ${smithFilter === '__none' ? 'None' : smithFilter}`, clear: () => setSmithFilter('') },
          custFilter && { k: 'cust', label: `Customer: ${custFilter === '__none' ? 'None' : custFilter}`, clear: () => setCustFilter('') },
          madeFor && view !== 'sold' && { k: 'madefor', label: 'Made for customers, not delivered', clear: () => setMadeFor(false) },
          zeroOn && { k: 'zeropcs', label: '0 pieces on tag', clear: () => setZeroOnly(false) },
          newOnly && { k: 'newpcs', label: `New pieces (${newOnly.size})`, clear: () => setNewOnly(null) },
          physOn && { k: 'phys', label: 'Physical pending', clear: () => setPhysOnly(false) },
          (dateFrom || dateTo) && { k: 'date', label: `Date: ${dateFrom || '…'} → ${dateTo || '…'}`, clear: () => { setDateFrom(''); setDateTo(''); } },
          search.trim() && { k: 'search', label: `Search: ${search.trim()}`, clear: () => setSearch('') },
        ]} count={filtered.length} onPrint={printFiltered} />
      </>}
    />
  );

  return (
    <TagWorkspaceShell
      variant={headerVariant}
      principal={principal} companyName={ctx?.company_name} isTagAdmin={!!ctx?.is_tag_admin}
      onLogout={() => { SALES_MODE.on = false; localStorage.removeItem('tagws_company'); logout(); }} toolbar={portalLocked || onDash ? null : toolbar}
      wide={onDash}
      headerExtra={isTagSuper && !portalLocked ? (
        <TagSuperBar companies={companies} companyId={ctx?.company_id} onCompany={chooseCompany} onPasswords={() => setPwOpen(true)} />
      ) : null}
      rail={{
        onSales: (portalPartial || (canSellNow && salesMode) || salesOnly) && !portalLocked ? () => setSalesOpen(true) : undefined,
        onApprovals: (portalPartial || ctx?.approvals_access) && !portalLocked ? () => setApprovalsOpen(true) : undefined,
        onPrint: () => (filtered.length ? setBatchOpen(true) : toast.info('No tags to print yet.')),
        onImport: salesOnly ? undefined : () => setImportOpen(true),
        onHelp: () => setHelpOpen(true),
        onShortcuts: ctx && !portalLocked ? () => setKeysOpen(true) : undefined,
        onSettings: isTagSuper && !portalLocked ? () => setSettingsOpen(true) : undefined,
        onTags: () => { closeAll(); if (onDash) setDash(false); else window.scrollTo({ top: 0, behavior: 'smooth' }); },
        tagsActive: !onDash,
        onDashboard: dashAllowed && !portalLocked ? () => { closeAll(); setDash(true); } : undefined,
        dashLabel: isTagSuper ? 'Stock dashboard (Alt + G, H)' : undefined,
        dashActive: onDash,
      }}
    >
      <TagImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={loadAll} />
      <TagHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <TagViewPasswordsAdmin open={pwOpen && isTagSuper} onClose={() => setPwOpen(false)} />
      <TagRulesAdmin open={rulesOpen && isTagSuper} onClose={() => setRulesOpen(false)} onChanged={loadRules} classifications={masters.classifications || []} items={items} defaultId={pageItems[0]?.id}
        usage={ruleUsage} onShowRule={(rid) => { setSearch(''); setDateFrom(''); setDateTo(''); setRuleFilter(rid); setRulesOpen(false); }} />
      <TagUsersAdmin open={usersOpen} companies={companies} onClose={() => setUsersOpen(false)} />
      {keysOpen ? <TagShortcutMenu ctx={ctx} onClose={() => setKeysOpen(false)} /> : null}
      {settingsOpen && isTagSuper && !portalLocked ? <TagSettingsDialog onClose={() => setSettingsOpen(false)} onUsers={() => setUsersOpen(true)} onRules={() => setRulesOpen(true)} /> : null}
      {backupOpen ? <TagBackupDialog onClose={() => setBackupOpen(false)} onRestored={() => { loadAll(); loadRules(); }} /> : null}
      {lockOpen ? <TagPortalLockDialog onClose={closeLock} /> : null}
      <TagSummaryAskHost />
      <TagAssistiveTouch ctx={ctx} />
      {isTagSuper && !portalLocked ? <TagLockFab onOpen={openLock} /> : null}
      {onDash ? <DashAccess.Provider value={dashAccess}>{isTagSuper ? <TagStockDashboard items={setAware} loadingMore={loadingMore} onTags={() => setDash(false)} onRefresh={softRefresh} /> : <TagMyDashboard items={setAware} grants={myGrants} name={principal?.name} onTags={() => setDash(false)} onRefresh={softRefresh} />}</DashAccess.Provider> : null}
      {onDash ? null : <>
      {!portalLocked && !loading && !error ? (<>
        {salesMode && canSellNow ? (
          <div className={`mb-4 flex items-center gap-3 ${selected.length ? 'md:mr-[19rem] 2xl:mr-0' : ''} ${isTagSuper && localStorage.getItem('tagws_stock_panel_open') !== '0' ? 'xl:ml-[16rem] min-[1900px]:ml-0' : ''}`} data-testid="tagws-stock-sold-tabs">
            <div className="flex rounded-xl border border-border bg-card p-1 shadow-sm">
              {[['stock', 'In stock', items.filter((j) => !j.sold && brandOk(j)).length], ['sold', 'Sold', items.filter((j) => j.sold && brandOk(j)).length]].map(([id, label, n]) => (
                <button key={id} type="button" onClick={() => { setView(id); setPage(1); setSoldCust(''); setSoldSales(''); }} aria-pressed={view === id}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${view === id ? (id === 'sold' ? 'bg-emerald-700 text-white shadow' : 'bg-primary text-primary-foreground shadow') : 'text-muted-foreground hover:text-foreground'}`} data-testid={`tagws-tab-${id}`}>
                  {label}<span className={`rounded-full px-1.5 font-mono text-[length:calc(var(--fs,1)*0.6875rem)] ${view === id ? 'bg-white/20' : 'bg-secondary'}`}>{n}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </>) : null}
      {portalPartial && !portalLocked ? <TagPhraseUnlock /> : null}
      {portalLocked ? (
        <div className="min-h-[40vh]" data-testid="tagws-portal-locked-blank" />
      ) : loading ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4 md:gap-5" data-testid="tagws-loading-grid">
          {Array.from({ length: 8 }).map((_, i) => (<div key={i} className="h-72 animate-pulse rounded-xl border border-border bg-secondary/50" data-testid="tagws-loading-card" />))}
        </div>
      ) : error ? (
        <div className="mx-auto max-w-md rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center" data-testid="tagws-error-state">
          <AlertTriangle className="mx-auto mb-3 text-destructive" size={28} />
          <div className="font-medium text-foreground">{error}</div>
          <button onClick={loadAll} data-testid="tagws-error-retry-button" className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"><RotateCcw size={14} /> Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mx-auto max-w-md rounded-xl border border-border bg-card p-10 text-center shadow-sm" data-testid="tagws-empty-state">
          <Diamond className="mx-auto mb-3 text-[hsl(var(--ring))]" size={30} />
          <div className="font-medium text-foreground">{items.length === 0 ? (portalPartial ? 'No numbered approvals or sales' : 'No jewellery yet') : !viewTotal ? (view === 'sold' ? 'No sold pieces yet' : 'Everything is sold') : 'No matches'}</div>
          <p className="mt-1 text-sm text-muted-foreground">{items.length === 0 ? (portalPartial ? 'Pieces appear here once an approval has a challan no. or a sale has a bill no.' : 'Create your first manual tag to get started.') : !viewTotal ? (view === 'sold' ? 'Sold pieces appear here after a sale.' : 'Every piece has been sold.') : 'Try adjusting your search or date range.'}</p>
          {ctx && !salesOnly && view !== 'sold' && <button onClick={() => setManual({ open: true, mode: 'create', item: null })} data-testid="tagws-empty-create-button" className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"><Plus size={15} /> Create manual tag</button>}
        </div>
      ) : (
        <>
        <div className="hidden md:block"><TagFitCheck items={pageItems} onResults={setFit} /></div>
        <SelectAllShown shown={filtered} selIds={selIds} setSelected={setSelected} />
        <div className={`grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4 md:gap-5 ${selected.length ? 'md:mr-[19rem] 2xl:mr-0' : ''} ${isTagSuper && localStorage.getItem('tagws_stock_panel_open') !== '0' ? 'xl:ml-[16rem] min-[1900px]:ml-0' : ''}`} data-testid="tagws-jewellery-grid">
          {pageItems.map((it) => (
            <TagJewelleryCard key={it.id} item={it}
              fitBad={fit && fit.bad[it.id] ? `${fit.size.w}×${fit.size.h} mm` : ''}
              onEdit={(i) => setManual({ open: true, mode: 'edit', item: i })}
              onTag={setTagItem} onReplaceImage={replaceImage} onCharges={setChargesItem} showGoldsmith={showSmith} showCustomer={showCust} showDate={showDates} showReceivedBy={isTagSuper && showDates && showSmith && showCust} brands={cardBrands} onBrand={isTagSuper ? brandSaved : undefined} onParty={partySaved} selected={selIds.has(it.id)} onSelect={toggleSel} replacing={replacingId === it.id} readOnly={salesOnly} onRepairs={repairHistory} zeroPcs={zeroPcs[it.id] || 0} onZeroFix={(i) => openLineFix(i.id)}
              setMates={it.set_id ? setMembers[it.set_id] : null} onSelectSet={salesOnly ? undefined : selectSet}
              onReceived={canReceive ? markReceived : undefined} tagOk={isTagSuper}
              ruleNames={isTagSuper && tagRules.length ? (ruleHits[it.id] || []).map((rid) => ruleLabel(tagRules.find((r) => r.id === rid))) : null} />
          ))}
        </div>
        </>
      )}
      {!loading && !error && !portalLocked && (
        <>
        <TagPagination page={safePage} pageCount={pageCount} total={filtered.length} pageSize={PAGE_SIZE} onPage={goPage} />
        {loadingMore && !refreshing ? <div className="mt-2 text-center text-xs text-muted-foreground" data-testid="tagws-loading-more">Loading more pieces in the background… ({items.length} loaded)</div> : null}
        </>
      )}
      </>}

      {portalLocked ? null : <TagMobileNav onSales={(canSellNow && salesMode) || salesOnly ? () => setSalesOpen(true) : undefined} onLists={portalPartial ? undefined : () => setListsOpen(true)} onScan={() => { setEditList(null); setScanOpen(true); }} onPrint={() => (filtered.length ? setBatchOpen(true) : toast.info('No tags to print yet.'))} onMore={() => setMoreOpen(true)} />}
      <TagMobileMore open={moreOpen} onClose={() => setMoreOpen(false)} superAdmin={isTagSuper} companies={companies} companyId={ctx?.company_id} onCompany={chooseCompany}
        actions={{ onCreate: salesOnly ? null : () => setManual({ open: true, mode: 'create', item: null }), onImport: salesOnly ? null : () => setImportOpen(true), onRates: ctx?.rates_view ? () => setRatesOpen(true) : null, onSettings: () => setSettingsOpen(true), onPasswords: () => setPwOpen(true), onLogout: () => { localStorage.removeItem('tagws_company'); logout(); } }} />
      <TagDeleteDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} items={items} onDeleted={reloadList} />
      {recvAsk ? <TagReceiveConfirm pieces={recvAsk} onClose={() => setRecvAsk(null)} onConfirm={confirmReceive} /> : null}
      {restoreOpen ? <TagRestoreDialog onClose={() => setRestoreOpen(false)} onRestored={reloadList} /> : null}
      <MissingRatesHost />
      {portalLocked || onDash ? null : <TagSelectionPanel selected={selected} itemsById={itemsById} onRemove={(id) => setSelected((cur) => cur.filter((x) => x.id !== id))}
        onClear={() => setSelected([])} onPrint={printSelected} onReceive={canReceive ? () => askReceive(selected.map((x) => itemsById.get(x.id))) : undefined} onAddToList={salesOnly ? undefined : () => { if (!recvBlock()) setAddToList(true); }} onSaveList={salesOnly ? undefined : () => { if (!recvBlock()) setListFromSel(true); }}
        canApprove={canApprove} canSellAp={!!canSellNow && salesMode} onApprove={() => { if (!repBlock()) setApproveFrom({ items: selected.filter((x) => !itemsById.get(x.id)?.sold && !itemsById.get(x.id)?.approval) }); }}
        onApprovalReturn={async () => {
          const ids = selected.filter((x) => itemsById.get(x.id)?.approval).map((x) => x.id);
          if (!window.confirm(`Record approval return for ${ids.length} piece(s)? They go back to stock.`)) return;
          try { await returnFromApproval(ids); toast.success(`${ids.length} piece(s) returned from approval.`); setSelected((cur) => cur.filter((x) => !ids.includes(x.id))); softRefresh(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the approval return.'); }
        }}
        onApprovalSell={async () => { try { setApSell(approvalLock(await loadApprovals(), selected.map((x) => x.id))); } catch { toast.error('Could not load the approval.'); } }}
        canSell={!!canSellNow && salesMode} onSell={() => { if (!repBlock()) setSaleOpen(true); }}
        onChangeCustomer={(isTagSuper || ctx?.is_sales) ? () => setCustChange(selected.map((x) => ({ id: x.id }))) : undefined} onReturn={async () => {
          const ids = selected.filter((x) => itemsById.get(x.id)?.sold).map((x) => x.id);
          const r = await openReturn({ ids, thumbs: new Map(ids.map((id) => [id, itemsById.get(id)?.image || itemsById.get(id)?.fallback_image])) });
          if (r) { setSelected((cur) => cur.filter((x) => !ids.includes(x.id))); softRefresh(); }
        }} />}
      {saleOpen ? <TagSaleDialog selected={selected.filter((x) => !itemsById.get(x.id)?.sold)} onClose={() => setSaleOpen(false)} onDone={() => { setSaleOpen(false); setSelected([]); softRefresh(); }} /> : null}
      {delAsk && selected.length ? <TagDeleteSelected pieces={selected.map((x) => { const j = itemsById.get(x.id); return { id: x.id, jewellery_no: j?.jewellery_no || x.jewellery_no, thumb: j?.image || j?.fallback_image }; })} onClose={() => setDelAsk(false)} onDeleted={(ids) => { setDelAsk(false); setSelected((cur) => cur.filter((x) => !ids.includes(x.id))); reloadList(); }} /> : null}
      {saleFrom ? <TagSaleDialog selected={saleFrom.items} cardId={saleFrom.cardId} stoneRates={saleFrom.stoneRates} title={saleFrom.title} onClose={() => setSaleFrom(null)} onDone={() => { setSaleFrom(null); softRefresh(); }} /> : null}
      <TagReturnDialogHost />
      {custChange ? <TagCustomerChangeDialog pieces={custChange} onClose={() => setCustChange(null)} onDone={() => { setCustChange(null); softRefresh(); }} /> : null}
      {repairOf ? <TagRepairHistoryDialog piece={repairOf} onClose={() => setRepairOf(null)} /> : null}
      {approveFrom ? <TagSaleDialog mode="approval" companyName={ctx?.company_name || ""} selected={approveFrom.items} cardId={approveFrom.cardId} stoneRates={approveFrom.stoneRates} title={approveFrom.title || ''} onClose={() => setApproveFrom(null)} onDone={() => { setApproveFrom(null); setSelected([]); softRefresh(); }} /> : null}
      {apSell ? <TagSaleDialog selected={apSell.items.map((i) => ({ id: i.id, jewellery_no: i.jewellery_no }))} lock={apSell} onClose={() => setApSell(null)} onDone={() => { setApSell(null); setSelected([]); softRefresh(); }} /> : null}
      {approvalsOpen ? <TagApprovalsScreen items={items} isSuper={isTagSuper} companyName={ctx?.company_name || ''} canSell={!!canSellNow && salesMode} readOnly={salesOnly} meId={ctx?.id} initial={apInit} onClose={() => { setApprovalsOpen(false); setApInit(null); }} onChanged={softRefresh} /> : null}
      {salesOpen ? <TagSalesScreen initialTab={salesTab} items={items} isSuper={isTagSuper} multiBrand={multiBrand} readOnly={salesOnly} meId={ctx?.id} company={ctx?.company_name || 'Personal'} onClose={() => { setSalesOpen(false); setSalesTab(''); }} onChanged={softRefresh} /> : null}
      {isTagSuper && !portalLocked && !loading && !onDash ? <TagStockPanel items={brandFilter ? items.filter(brandOk) : items} /> : null}
      {addToList === true ? <PickListDialog onClose={() => setAddToList(false)} onPick={(l) => setAddToList(mergeInto(l, selected))} /> : null}
      {addToList && addToList !== true ? (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-3" data-testid="tagws-add-to-list-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setAddToList(false)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-card p-4 shadow-xl">
            <TagListBuilder items={items} editList={addToList} onCancel={() => setAddToList(false)} onDone={() => setAddToList(false)} />
          </div>
        </div>
      ) : null}
      {listFromSel ? (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-3" data-testid="tagws-selection-list-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setListFromSel(false)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-card p-4 shadow-xl">
            <TagListBuilder items={items} initialItems={selected.slice(0, 30)} onCancel={() => setListFromSel(false)} onDone={() => setListFromSel(false)} />
          </div>
        </div>
      ) : null}
      <TagChargesDialog item={chargesItem} onClose={() => setChargesItem(null)} onSaved={chargesSaved} />
      <TagRateCards open={ratesOpen} onClose={() => setRatesOpen(false)} items={items} />
      <TagScanDialog open={scanOpen} editList={editList} items={items} onSaved={() => setListsKey((k) => k + 1)} onClose={() => { setScanOpen(false); setEditList(null); }}
        onViewTags={(ids, f) => { setScanOpen(false); viewIds(ids, f); }} />
      <TagSavedLists open={listsOpen} readOnly={salesOnly} reloadKey={listsKey} items={items} onClose={() => setListsOpen(false)}
        onView={(l) => { setListsOpen(false); if (l.rate_card_id) setPricing({ cardId: l.rate_card_id }); applyListStones(l.stone_rates); viewIds(l.items.map((x) => x.id), { rmMode: l.rm_mode, showPieces: l.show_pieces, listName: l.name, list: l }); }}
        onSell={canSellNow && salesMode ? (l) => {
          const live = l.items.filter((x) => !itemsById.get(x.id)?.sold && !itemsById.get(x.id)?.repair?.in_repair);
          if (!live.length) { toast.info('Every piece in this list is sold or sent for repairing.'); return; }
          if (live.length < l.items.length) toast.info(`${l.items.length - live.length} piece(s) sold or sent for repairing were left out.`);
          setListsOpen(false); setSaleFrom({ items: live, cardId: l.rate_card_id || '', stoneRates: l.stone_rates || {}, title: l.name });
        } : undefined}
        onApprove={canApprove ? (l) => {
          const live = l.items.filter((x) => !itemsById.get(x.id)?.sold && !itemsById.get(x.id)?.approval && !itemsById.get(x.id)?.repair?.in_repair);
          if (!live.length) { toast.info('Every piece in this list is sold, on approval or sent for repairing.'); return; }
          if (live.length < l.items.length) toast.info(`${l.items.length - live.length} piece(s) sold, on approval or sent for repairing were left out.`);
          setListsOpen(false); setApproveFrom({ items: live, cardId: l.rate_card_id || '', stoneRates: l.stone_rates || {}, title: l.name });
        } : undefined}
        onEdit={(l) => { setListsOpen(false); setEditList(l); setScanOpen(true); }} onScanNew={() => { setListsOpen(false); setEditList(null); setScanOpen(true); }} />
      <TagLineFixHost />
      {fixLogOpen ? <TagFixLogDialog onClose={() => setFixLogOpen(false)} /> : null}
      {freshIds ? <TagNewPiecesNotice count={freshIds.length} onClose={closeFresh} onShow={() => { setNewOnly(new Set(freshIds)); setView('stock'); closeFresh(); }} /> : null}
      <TagSingleTagDialog item={tagItem} onClose={() => setTagItem(null)} onLayoutSaved={rememberLayout} />

      <JewelleryBatchPrintDialog
        open={batchOpen} raised={approvalsOpen} onOpenChange={(v) => { setBatchOpen(v); if (!v) { setScanIds(null); setScanFmt(null); } }} items={(scanIds ? items : filtered).filter((j) => !notReceived(j))} initialSelectedIds={scanIds || []}
        listName={scanFmt?.listName} onStoneRates={scanFmt?.list ? (sr) => persistListStones(scanFmt.list, sr) : undefined} initialRmMode={scanFmt?.rmMode} initialShowPieces={scanFmt?.showPieces} initialPreview={!!scanFmt}
        apiClient={notesApi} batchEndpoint="/notes/tag-workspace/tags/batch" sizeToggle
        rememberLayouts onLayoutSaved={rememberLayout}
      />

      <TagManualTagDialog
        open={manual.open} mode={manual.mode} initial={manual.item} masters={masters}
        onClose={() => setManual((m) => ({ ...m, open: false }))} onSaved={reloadList}
      />

      {deleteTarget && (
        <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[200] flex items-center justify-center p-4`} data-testid="tagws-delete-confirmation-dialog">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={() => !deleting && setDeleteTarget(null)} />
          <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-destructive/10 text-destructive"><Trash2 size={18} /></span>
              <div>
                <h3 className="text-base font-semibold text-foreground">Delete manual tag?</h3>
                <p className="mt-1 text-sm text-muted-foreground">This permanently removes <span className="font-mono text-foreground">{deleteTarget.jewellery_no}</span> from Tag Workspace. This cannot be undone.</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => !deleting && setDeleteTarget(null)} data-testid="tagws-delete-cancel-button" className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm text-foreground hover:bg-secondary">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} data-testid="tagws-delete-confirmation-button" className="inline-flex items-center gap-1.5 rounded-lg bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-60 active:scale-[0.98]">{deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete</button>
            </div>
          </div>
        </div>
      )}
    </TagWorkspaceShell>
  );
}
